import React, { useState, useEffect, useCallback, useMemo } from "react";
import {
  Box,
  Typography,
  TextField,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormControl,
  Dialog,
  DialogContent,
  IconButton,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { useNavigate } from "react-router-dom";
import { loadStripe } from "@stripe/stripe-js";
import { useTranslation } from "react-i18next";

import {
  createStripeCheckoutSession,
  getUserEmail,
  getBirthdayPageData,
  getUnconfirmedPayPayPayment,
} from "../../../utils/api";
import AppLayout from "../../../default/components/AppLayout";
import InputEmail from "../../../default/components/InputEmail";
import PayPayButton from "../../../default/components/PayPayButton";
import StyledButton from "../../../default/components/StyledButton";
import TicketNotFound from "../../../default/components/TicketNotFound";
import Loading from "../../../default/components/Loading";
import { useUrlParams } from "../../../hooks/useUrlParams";
import { useTheme } from "@emotion/react";

// Constants
const STRIPE_API_KEY = process.env.REACT_APP_STRIPE_API_KEY;
const PURCHASE_METHODS = {
  STRIPE: "stripe",
  PAYPAY: "paypay",
};

// Stripe initialization - outside component to avoid recreation
const stripePromise = loadStripe(STRIPE_API_KEY);

const NIGHT_21 = "2025-09-23 21:00:00"; // 21時配達
const NIGHT_23 = "2025-09-23 23:45:00"; // 23時45分配達
const MIDNIGHT_00 = "2025-09-23 00:00:00"; // 0時配達

// 配達時間選択肢の表示判定関数
const isDeliveryTimeAvailable = (deliveryTimeString) => {
  const now = new Date();
  const tokyoTime = new Date(
    now.toLocaleString("en-US", { timeZone: "Asia/Tokyo" })
  );
  const deliveryTime = new Date(deliveryTimeString);

  // 配達時刻の3分前までは選択可能とする
  const cutoffTime = new Date(deliveryTime.getTime() - 3 * 60 * 1000);

  return tokyoTime <= cutoffTime;
};

const withLng = (path, lng) => {
  const hasQuery = path.includes("?");
  return lng ? `${path}${hasQuery ? "&" : "?"}lng=${lng}` : path;
};

// QuestionIcon Component
const QuestionIcon = ({ onClick }) => (
  <Box
    onClick={onClick}
    sx={(theme) => ({
      width: "16px",
      height: "16px",
      borderRadius: "50%",
      border: `1px solid ${theme.typography.color.primary}`,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      cursor: "pointer",
      fontSize: "10px",
      color: theme.typography.color.primary,
      fontWeight: "bold",
      ml: 1,
      mt: -0.5,
    })}
  >
    ?
  </Box>
);

// Common Dialog Component
const CommonDialog = ({
  open,
  onClose,
  title,
  children,
  theme,
  maxWidth = "350px",
}) => {
  return (
    <Dialog
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          backgroundColor: "transparent",
          boxShadow: "none",
          width: "88%",
        },
      }}
    >
      <DialogContent sx={{ padding: 0 }}>
        <Box
          sx={{
            backgroundColor: "rgba(0, 0, 0, 0.9)",
            border: "1px solid rgba(255, 255, 255, 0.6)",
            boxShadow:
              "0 0 15px rgba(255, 255, 255, 0.4), 0 1px 1px rgba(255, 255, 255, 0.4)",
            borderRadius: "8px",
            padding: "32px 24px",
            maxWidth,
            position: "relative",
          }}
        >
          <IconButton
            onClick={onClose}
            sx={{
              position: "absolute",
              right: "8px",
              top: "8px",
              color: theme.typography.color.primary,
            }}
          >
            <CloseIcon />
          </IconButton>
          <Typography
            variant="h6"
            sx={{
              fontFamily: theme.typography.header.fontFamily,
              fontSize: "16px",
              fontWeight: "bold",
              color: theme.typography.color.primary,
              mb: 3,
              textAlign: "center",
            }}
          >
            {title}
          </Typography>
          {children}
        </Box>
      </DialogContent>
    </Dialog>
  );
};

// InfoDialog Component
const InfoDialog = ({ open, onClose, type, theme }) => {
  const getContent = () => {
    switch (type) {
      case "beer":
        return {
          title: "誕生日プレゼント",
          content: (
            <>
              ナズナの誕生日（9月23日）に
              <br />
              お祝いメッセージと共に缶ビールを
              <br />
              お届けします。
              <br />
              <br />
              ナズナから画像を送ってもらう特別クエストが開放されます。
            </>
          ),
        };
      case "message":
        return {
          title: "メッセージについて",
          content: (
            <>
              送り主が分かるように、お名前を含め、ナズナに伝えたいお祝いのメッセージを書いてください。
              <br />
              ※50文字以内
            </>
          ),
        };
      case "delivery":
        return {
          title: "配達日時について",
          content: (
            <>
              選択した時間にプレゼントが届きます。選択した時間に返信ができなくても、
              <br />
              後から返信できます。
            </>
          ),
        };
      default:
        return { title: "", content: "" };
    }
  };

  const { title, content } = getContent();

  return (
    <CommonDialog open={open} onClose={onClose} title={title} theme={theme}>
      <Typography
        sx={{
          fontFamily: theme.typography.fontFamily.body,
          fontSize: "14px",
          color: theme.typography.color.primary,
          lineHeight: 1.8,
          textAlign: "center",
        }}
      >
        {content}
      </Typography>
    </CommonDialog>
  );
};

const usePurchase = (soulId, work) => {
  const navigate = useNavigate();
  const [state, setState] = useState({
    isProcessing: false,
    presentData: null,
    purchaseMethod: null,
    emailRequired: false,
  });

  const purchaseWithStripe = useCallback(
    async (presentData) => {
      try {
        const { session_id } = await createStripeCheckoutSession(soulId, {
          product_type: "present",
          data: presentData,
        });
        const stripe = await stripePromise;

        const { error } = await stripe.redirectToCheckout({
          sessionId: session_id,
        });

        if (error) {
          console.error("Stripe error:", error);
        }
      } catch (error) {
        console.error("Purchase error:", error);
      }
    },
    [soulId]
  );

  const purchaseWithPayPay = useCallback(
    (presentData) => {
      const params = new URLSearchParams({
        soul_id: soulId,
        product_type: "present",
        data: JSON.stringify(presentData),
      });
      navigate(`/${work}/paypay/confirm?${params.toString()}`);
    },
    [navigate, soulId, work]
  );

  const handlePurchaseMethod = useCallback(
    async (method, presentData) => {
      setState((prev) => ({ ...prev, isProcessing: true }));

      try {
        const email = await getUserEmail();

        if (!email.email_address) {
          setState((prev) => ({
            ...prev,
            emailRequired: true,
            purchaseMethod: method,
            presentData: presentData,
            isProcessing: false,
          }));
          window.scrollTo(0, 0);
          return;
        }

        if (method === PURCHASE_METHODS.STRIPE) {
          await purchaseWithStripe(presentData);
        } else if (method === PURCHASE_METHODS.PAYPAY) {
          purchaseWithPayPay(presentData);
        }
      } catch (error) {
        console.error("Purchase method error:", error);
      } finally {
        setState((prev) => ({ ...prev, isProcessing: false }));
      }
    },
    [purchaseWithStripe, purchaseWithPayPay]
  );

  const navigateToPurchase = useCallback(async () => {
    const { purchaseMethod, presentData } = state;

    if (purchaseMethod === PURCHASE_METHODS.STRIPE) {
      await purchaseWithStripe(presentData);
    } else if (purchaseMethod === PURCHASE_METHODS.PAYPAY) {
      purchaseWithPayPay(presentData);
    }
  }, [state, purchaseWithStripe, purchaseWithPayPay]);

  return {
    ...state,
    handlePurchaseMethod,
    navigateToPurchase,
    setPurchaseState: setState,
  };
};

// Component for Purchase Dialog
const PurchaseDialog = ({
  open,
  onClose,
  onPurchaseMethod,
  isProcessing,
  lng,
  t,
  theme,
}) => {
  return (
    <CommonDialog
      open={open}
      onClose={onClose}
      title={t("common:button.dialog.select_purchase")}
      theme={theme}
      maxWidth="450px"
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: 2,
          alignItems: "center",
        }}
      >
        <StyledButton
          variant="contained"
          onClick={() => onPurchaseMethod(PURCHASE_METHODS.STRIPE)}
          sx={(theme) => ({
            width: "80%",
            fontSize: "16px",
            color: theme.Dialog.button.color,
            border: `2px solid ${theme.Dialog.button.color}`,
            background: isProcessing ? "white" : "transparent",
          })}
          disabled={isProcessing}
        >
          {t("common:button.purchase_option_credit_card")}
        </StyledButton>

        {lng === "ja" && (
          <Box
            sx={{
              width: "80%",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
            }}
          >
            <PayPayButton
              onClick={() => onPurchaseMethod(PURCHASE_METHODS.PAYPAY)}
              disabled={isProcessing}
            />
            <Typography
              sx={{
                mt: 1,
                fontSize: "12px",
                color: theme.typography.color.primary,
                textAlign: "center",
                fontFamily: theme.typography.fontFamily.body,
                whiteSpace: "pre-line",
              }}
            >
              {t("common:button.purchase_option_paypay")}
            </Typography>
          </Box>
        )}
      </Box>
    </CommonDialog>
  );
};

// Main Component
function Birthday() {
  const navigate = useNavigate();
  const theme = useTheme();
  const { soulId, lng, work, character } = useUrlParams();
  const { t } = useTranslation([work, "common"]);

  const handleNavigateToTickets = () => {
    navigate(withLng(`/${work}/tickets?soul_id=${soulId}`, lng));
  };

  const {
    isProcessing,
    emailRequired,
    handlePurchaseMethod,
    navigateToPurchase,
    setPurchaseState,
  } = usePurchase(soulId, work);

  const [dialogState, setDialogState] = useState({
    openDialog: false,
  });

  // Birthday page data state
  const [pageData, setPageData] = useState({
    isLoading: true,
    ticketNotFound: false,
    alreadyPurchased: false,
  });

  // Payment check state
  const [paymentCheck, setPaymentCheck] = useState({
    merchantPaymentId: null,
    showPaymentCheck: false,
  });

  // Check for unconfirmed PayPay payment
  const checkUnconfirmedPayment = useCallback(async (soulId) => {
    try {
      const { merchant_payment_id: merchantPaymentId } =
        await getUnconfirmedPayPayPayment(soulId);

      if (merchantPaymentId) {
        setPaymentCheck({
          merchantPaymentId,
          showPaymentCheck: true,
        });
      }
    } catch (error) {
      console.error("Error checking unconfirmed payment:", error);
    }
  }, []);

  // New state for the form
  const [message, setMessage] = useState("");
  const [deliveryTime, setDeliveryTime] = useState("");
  const [infoDialog, setInfoDialog] = useState({ open: false, type: null });
  const [validationDialog, setValidationDialog] = useState({
    open: false,
    message: "",
  });
  const [isMessageFocused, setIsMessageFocused] = useState(false);

  // Check if selected delivery time is still available
  useEffect(() => {
    if (deliveryTime && !isDeliveryTimeAvailable(deliveryTime)) {
      setDeliveryTime("");
    }
  }, [deliveryTime]);

  // Fetch birthday page data on initialization
  useEffect(() => {
    const fetchBirthdayData = async () => {
      if (!soulId) return;

      // Check for unconfirmed payment first
      await checkUnconfirmedPayment(soulId);

      try {
        const data = await getBirthdayPageData(soulId);
        setPageData({
          isLoading: false,
          ticketNotFound: data.ticket_not_found,
          alreadyPurchased: data.already_purchased,
        });
      } catch (error) {
        console.error("Error fetching birthday data:", error);
        setPageData({
          isLoading: false,
          ticketNotFound: true,
          alreadyPurchased: false,
        });
      }
    };

    fetchBirthdayData();
  }, [soulId, checkUnconfirmedPayment]);

  const handleDialogPurchaseMethod = useCallback(
    (method) => {
      // Create present data from form inputs
      const presentData = {
        present_key: "beer",
        details: {
          message: message.trim(),
          delivery_time: deliveryTime,
        },
      };

      handlePurchaseMethod(method, presentData);
      setDialogState((prev) => ({ ...prev, openDialog: false }));
    },
    [handlePurchaseMethod, message, deliveryTime]
  );

  const handlePurchase = useCallback(() => {
    // Validate form inputs
    if (!message.trim()) {
      setValidationDialog({
        open: true,
        message: "メッセージを入力してください。",
      });
      return;
    }

    if (!deliveryTime) {
      setValidationDialog({
        open: true,
        message: "配達日時を選択してください。",
      });
      return;
    }

    // Open purchase method dialog directly
    setDialogState({
      openDialog: true,
    });
  }, [message, deliveryTime]);

  const handlePaymentCheckClose = useCallback(() => {
    setPaymentCheck((prev) => ({ ...prev, showPaymentCheck: false }));
  }, []);

  const navigateToPaymentCheck = useCallback(() => {
    navigate(
      `/${work}/paypay/check?soul_id=${soulId}&merchant_payment_id=${paymentCheck.merchantPaymentId}&lng=${lng}&success_page=mypage&fail_page=birthday`
    );
  }, [navigate, work, soulId, paymentCheck.merchantPaymentId, lng]);

  // Render loading state
  if (pageData.isLoading) {
    return (
      <AppLayout page="Birthday" width="100%">
        <Loading />
      </AppLayout>
    );
  }

  // Render ticket not found state
  if (pageData.ticketNotFound) {
    return (
      <AppLayout page="Birthday" width="88%">
        <TicketNotFound onClick={handleNavigateToTickets} />
      </AppLayout>
    );
  }

  // Render email input state
  if (emailRequired) {
    return (
      <AppLayout page="Birthday" width="88%">
        <InputEmail soulId={soulId} onClickPurchase={navigateToPurchase} />
      </AppLayout>
    );
  }

  // Render already purchased state
  if (pageData.alreadyPurchased) {
    return (
      <AppLayout page="Birthday" width="100%">
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            width: "100%",
            px: 3,
            py: 2,
          }}
        >
          {/* Logo */}
          <Box sx={{ alignSelf: "flex-start", mb: -5 }}>
            <img
              src={process.env.PUBLIC_URL + "/yofukashi/logo.png"}
              alt="logo"
              style={{ height: "70px" }}
            />
          </Box>

          {/* Beer Image */}
          <Box sx={{ mb: -3 }}>
            <img
              src={process.env.PUBLIC_URL + "/yofukashi/beer_drinking.png"}
              alt="beer drinking"
              style={{ maxWidth: "200px", height: "auto" }}
            />
          </Box>

          {/* Product Name */}
          <Box
            sx={{
              mb: 8,
              textAlign: "center",
              width: "100%",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
            }}
          >
            <Typography
              variant="h4"
              sx={(theme) => ({
                fontFamily: theme.typography.header.fontFamily,
                fontSize: "24px",
                fontWeight: "bold",
                color: theme.typography.color.primary,
                lineHeight: 1.5,
              })}
            >
              缶ビール 1本
            </Typography>
          </Box>

          {/* Already Purchased Message */}
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              width: "100%",
            }}
          >
            <Typography
              variant="h4"
              sx={(theme) => ({
                fontFamily: theme.typography.header.fontFamily,
                fontSize: "24px",
                fontWeight: "bold",
                color: theme.typography.color.primary,
                textAlign: "center",
                mb: 2,
              })}
            >
              配達を手配済みです
            </Typography>
            <Typography
              sx={(theme) => ({
                fontFamily: theme.typography.fontFamily.body,
                fontSize: "16px",
                color: theme.typography.color.primary,
                textAlign: "center",
                whiteSpace: "pre-line",
                opacity: 0.8,
              })}
            >
              {"\n"}ご指定の日時に{"\n"}プレゼントをお届けします
            </Typography>
          </Box>
        </Box>
      </AppLayout>
    );
  }

  // Main render (only shown when already_purchased is false)
  return (
    <AppLayout page="Birthday" width="100%">
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          width: "100%",
          px: 3,
          py: 2,
        }}
      >
        {/* Logo */}
        <Box sx={{ alignSelf: "flex-start", mb: -5 }}>
          <img
            src={process.env.PUBLIC_URL + "/yofukashi/logo.png"}
            alt="logo"
            style={{ height: "70px" }}
          />
        </Box>

        {/* Beer Image */}
        <Box sx={{ mb: -3 }}>
          <img
            src={process.env.PUBLIC_URL + "/yofukashi/beer_drinking.png"}
            alt="beer drinking"
            style={{ maxWidth: "200px", height: "auto" }}
          />
        </Box>

        {/* Price */}
        <Box
          sx={{
            mb: 4,
            textAlign: "center",
            width: "100%",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "flex-start" }}>
            <Typography
              variant="h4"
              sx={(theme) => ({
                fontFamily: theme.typography.header.fontFamily,
                fontSize: "24px",
                fontWeight: "bold",
                color: theme.typography.color.primary,
                lineHeight: 1.5,
              })}
            >
              缶ビール 1本
              <br />
              ￥330
            </Typography>
            <QuestionIcon
              onClick={() => setInfoDialog({ open: true, type: "beer" })}
            />
          </Box>
        </Box>

        {/* Message Section */}
        <Box
          sx={{
            width: "100%",
            maxWidth: "400px",
            mb: 4,
            textAlign: "center",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "flex-start",
              justifyContent: "center",
              mb: 2,
            }}
          >
            <Typography
              variant="h6"
              sx={(theme) => ({
                fontFamily: theme.typography.header.fontFamily,
                fontSize: "22px",
                fontWeight: "bold",
                color: theme.typography.color.primary,
              })}
            >
              メッセージ
            </Typography>
            <QuestionIcon
              onClick={() => setInfoDialog({ open: true, type: "message" })}
            />
          </Box>
          <Box
            sx={(theme) => ({
              width: "92%",
              backgroundColor: "rgba(0, 0, 0, 0.05)",
              border: "1px solid rgba(255, 255, 255, 0.6)",
              boxShadow:
                "0 0 15px rgba(255, 255, 255, 0.4), 0 1px 1px rgba(255, 255, 255, 0.4)",
              borderRadius: "4px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              minHeight: "120px",
              padding: "8px",
            })}
          >
            <TextField
              multiline
              fullWidth
              value={message}
              onChange={(e) => {
                if (e.target.value.length <= 50) {
                  setMessage(e.target.value);
                }
              }}
              onFocus={() => setIsMessageFocused(true)}
              onBlur={() => setIsMessageFocused(false)}
              placeholder={
                isMessageFocused ? "" : "誕生日おめでとう！\n-太郎より-"
              }
              sx={(theme) => ({
                "& .MuiOutlinedInput-root": {
                  border: "none",
                  "& fieldset": {
                    border: "none",
                  },
                  "&:hover fieldset": {
                    border: "none",
                  },
                  "&.Mui-focused fieldset": {
                    border: "none",
                  },
                },
                "& .MuiInputBase-input": {
                  textAlign: "center",
                  color: theme.typography.color.primary,
                  fontFamily: theme.typography.fontFamily.body,
                },
                "& .MuiInputBase-inputMultiline": {
                  textAlign: "center",
                  color: theme.typography.color.primary,
                  fontFamily: theme.typography.fontFamily.body,
                  resize: "none",
                },
                "& input::placeholder, & textarea::placeholder": {
                  color: theme.typography.color.primary,
                  opacity: 0.7,
                  textAlign: "center",
                  fontFamily: theme.typography.fontFamily.body,
                },
              })}
            />
          </Box>
          <Box
            sx={{ width: "92%", display: "flex", justifyContent: "flex-end" }}
          >
            <Typography
              variant="caption"
              sx={(theme) => ({
                mt: 1,
                color: theme.typography.color.primary,
                opacity: 0.7,
                fontFamily: theme.typography.fontFamily.body,
              })}
            >
              {message.length}/50
            </Typography>
          </Box>
        </Box>

        {/* Delivery Time Section */}
        <Box
          sx={{ width: "100%", maxWidth: "400px", mb: 4, textAlign: "center" }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "flex-start",
              justifyContent: "center",
              mb: 2,
            }}
          >
            <Typography
              variant="h6"
              sx={(theme) => ({
                fontFamily: theme.typography.header.fontFamily,
                fontSize: "22px",
                fontWeight: "bold",
                color: theme.typography.color.primary,
              })}
            >
              配達日時
            </Typography>
            <QuestionIcon
              onClick={() => setInfoDialog({ open: true, type: "delivery" })}
            />
          </Box>
          <FormControl component="fieldset">
            <RadioGroup
              value={deliveryTime}
              onChange={(e) => setDeliveryTime(e.target.value)}
            >
              {isDeliveryTimeAvailable(MIDNIGHT_00) && (
                <FormControlLabel
                  value={MIDNIGHT_00}
                  sx={{
                    mb: 1,
                  }}
                  control={
                    <Radio
                      sx={(theme) => ({
                        color: "white",
                        "&.Mui-checked": {
                          color: theme.typography.color.primary,
                        },
                      })}
                    />
                  }
                  label={
                    <Typography
                      sx={(theme) => ({
                        color: theme.typography.color.primary,
                        fontFamily: theme.typography.fontFamily.body,
                        fontSize: "16px",
                      })}
                    >
                      9月23日 00:00頃
                    </Typography>
                  }
                />
              )}
              {isDeliveryTimeAvailable(NIGHT_21) && (
                <FormControlLabel
                  value={NIGHT_21}
                  control={
                    <Radio
                      sx={(theme) => ({
                        color: "white",
                        "&.Mui-checked": {
                          color: theme.typography.color.primary,
                        },
                      })}
                    />
                  }
                  label={
                    <Typography
                      sx={(theme) => ({
                        color: theme.typography.color.primary,
                        fontFamily: theme.typography.fontFamily.body,
                        fontSize: "16px",
                      })}
                    >
                      9月23日 21:00頃
                    </Typography>
                  }
                />
              )}
              {isDeliveryTimeAvailable(NIGHT_23) && (
                <FormControlLabel
                  value={NIGHT_23}
                  control={
                    <Radio
                      sx={(theme) => ({
                        color: "white",
                        "&.Mui-checked": {
                          color: theme.typography.color.primary,
                        },
                      })}
                    />
                  }
                  label={
                    <Typography
                      sx={(theme) => ({
                        color: theme.typography.color.primary,
                        fontFamily: theme.typography.fontFamily.body,
                        fontSize: "16px",
                      })}
                    >
                      9月23日 23:45頃
                    </Typography>
                  }
                />
              )}
            </RadioGroup>
            {!isDeliveryTimeAvailable(MIDNIGHT_00) &&
              !isDeliveryTimeAvailable(NIGHT_21) &&
              !isDeliveryTimeAvailable(NIGHT_23) && (
                <Typography
                  sx={(theme) => ({
                    color: theme.typography.color.primary,
                    fontFamily: theme.typography.fontFamily.body,
                    fontSize: "16px",
                    textAlign: "center",
                    opacity: 0.7,
                    mt: 2,
                  })}
                >
                  配達可能な時間帯が終了しました
                </Typography>
              )}
          </FormControl>
        </Box>

        {/* Purchase Button */}
        <StyledButton
          variant="contained"
          onClick={handlePurchase}
          disabled={
            isProcessing ||
            (!isDeliveryTimeAvailable(MIDNIGHT_00) &&
              !isDeliveryTimeAvailable(NIGHT_21) &&
              !isDeliveryTimeAvailable(NIGHT_23))
          }
          sx={(theme) => ({
            width: "100%",
            maxWidth: "300px",
            fontSize: "18px",
            fontWeight: "bold",
            py: 2,
            mb: 4,
            border: `2px solid ${theme.Dialog.button.color}`,
            background: isProcessing ? "white" : "transparent",
            opacity:
              !isDeliveryTimeAvailable(MIDNIGHT_00) &&
              !isDeliveryTimeAvailable(NIGHT_21) &&
              !isDeliveryTimeAvailable(NIGHT_23)
                ? 0.5
                : 1,
          })}
        >
          プレゼントを購入
        </StyledButton>
      </Box>

      {/* Purchase Method Dialog */}
      <PurchaseDialog
        open={dialogState.openDialog}
        onClose={() =>
          setDialogState((prev) => ({ ...prev, openDialog: false }))
        }
        onPurchaseMethod={handleDialogPurchaseMethod}
        isProcessing={isProcessing}
        lng={lng}
        t={t}
        theme={theme}
      />

      {/* Info Dialog */}
      <InfoDialog
        open={infoDialog.open}
        onClose={() => setInfoDialog({ open: false, type: null })}
        type={infoDialog.type}
        theme={theme}
      />

      {/* Payment Check Dialog */}
      <CommonDialog
        open={paymentCheck.showPaymentCheck}
        onClose={handlePaymentCheckClose}
        title="PayPay購入による処理が正常に完了していない可能性があります。"
        theme={theme}
        maxWidth="400px"
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: 2,
            alignItems: "center",
          }}
        >
          <StyledButton
            variant="contained"
            onClick={navigateToPaymentCheck}
            sx={(theme) => ({
              width: "80%",
              fontSize: "16px",
              color: theme.Dialog.button.color,
              border: `2px solid ${theme.Dialog.button.color}`,
              background: "transparent",
            })}
          >
            購入状況を確認
          </StyledButton>
        </Box>
      </CommonDialog>

      {/* Validation Dialog */}
      <CommonDialog
        open={validationDialog.open}
        onClose={() => setValidationDialog({ open: false, message: "" })}
        title="入力エラー"
        theme={theme}
      >
        <Typography
          sx={{
            fontFamily: theme.typography.fontFamily.body,
            fontSize: "14px",
            color: theme.typography.color.primary,
            lineHeight: 1.8,
            textAlign: "center",
          }}
        >
          {validationDialog.message}
        </Typography>
      </CommonDialog>
    </AppLayout>
  );
}

export default Birthday;
